import { useEffect, useRef } from 'react';

/**
 * useAutoPreview — Envia patches automáticos ao iframe de preview (§9.2 do plano).
 *
 * Dispara para mudanças estruturais/estilo: templateId, layoutMode, customStyles,
 * scope, blocks[]. Texto livre (seo.*, identity.*) fica para o botão Preview (P15).
 *
 * IMPORTANTE: usa `form.watch(callback)` em vez de `useWatch`. Com `useWatch`, o
 * objeto retornado (customStyles, scope) mantém a MESMA referência quando um campo
 * aninhado muda (RHF muta in-place), então o useEffect não detectava a mudança e o
 * patch não era enviado — por isso alterar só uma cor/fonte não refletia no preview.
 * `form.watch(callback)` dispara em qualquer alteração, inclusive aninhada.
 *
 * Debounce: 150ms.
 *
 * @param {object} form - instância do react-hook-form com siteFormSchema
 * @param {React.MutableRefObject} previewRef - ref expondo sendPatch() do SitePreview
 */
const AUTO_PREVIEW_FIELDS = ['templateId', 'layoutMode', 'customStyles', 'scope', 'blocks'];

function matchesAutoPreview(name) {
  if (!name) return false; // reset/programático — o FULL_REFRESH do load cuida do estado inicial
  return AUTO_PREVIEW_FIELDS.some(
    (f) => name === f || name.startsWith(`${f}.`) || name.startsWith(`${f}[`)
  );
}

export default function useAutoPreview(form, previewRef) {
  const timerRef = useRef(null);

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (!matchesAutoPreview(name)) return;

      // customStyles sempre no patch: é barato (o template aplica via CSS vars sem
      // re-render) e garante que o estilo não se perca num re-render estrutural.
      // blocks/scope entram só quando mudam — assim alterar só estilo NÃO força
      // re-render do iframe (sem flicker; cores/fontes mudam na hora).
      const patch = { customStyles: values.customStyles };
      if (name.startsWith('blocks')) patch.blocks = values.blocks;
      if (name.startsWith('scope')) patch.scope = values.scope;

      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        previewRef.current?.sendPatch(patch);
      }, 150);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timerRef.current);
    };
  }, [form, previewRef]);
}
